import { Clock, Context, Effect, Schedule, Stream } from "effect";
import * as Dig from "../game/dig.ts";
import * as GameClock from "../game/clock.ts";
import * as Song from "../game/song.ts";
import type * as Errors from "../shared/errors.ts";
import * as Protocol from "../shared/protocol.ts";

// One text frame as the boundary took it off the socket, stamped with the client clock right
// there: a stamp taken after queueing would charge a busy tab's delay to the round trip.
export type Frame = {
  readonly text: string;
  readonly at: number;
};

// The open socket to the room: writes, and every frame it delivers until it closes.
export type LinkShape = {
  readonly send: (text: string) => Effect.Effect<void, Errors.SendFailed>;
  readonly messages: Stream.Stream<Frame, Errors.Disconnected | Errors.BadMessage>;
};

export class Link extends Context.Service<Link, LinkShape>()("@beatdig/client/Link") {}

// The keys pressed on the page, each named as the browser names it ("a", "Enter", "ArrowDown"),
// for as long as the page is open.
export type ControlsShape = {
  readonly keys: Stream.Stream<string>;
};

export class Controls extends Context.Service<Controls, ControlsShape>()(
  "@beatdig/client/Controls",
) {}

// What the render loop paints from: the world as last told, which of its players is this screen's
// (`you`, a slot), the name typed so far, and the clock estimate (`offset` is server minus client;
// `samples` is 0 until the first pong).
export type View = {
  readonly world: Dig.World;
  readonly you: number;
  readonly draft: string;
  readonly offset: number;
  readonly rtt: number;
  readonly samples: number;
};

export const WAITING: View = {
  world: Dig.EMPTY,
  you: 0,
  draft: "",
  offset: 0,
  rtt: 0,
  samples: 0,
};

export type ScreenShape = {
  readonly show: (view: View) => void;
};

export class Screen extends Context.Service<Screen, ScreenShape>()("@beatdig/client/Screen") {}

// What sounds the song. `play` takes the notes of one stretch of server time, the server time
// `until` the stretch reaches, and the clock estimate's `offset` (server minus client), so the
// boundary can place each note on its own output clock; while the page may not make sound yet
// (no click has unlocked it) the batch is dropped. `placed` is the server time up to which the
// speaker has taken the song; the speaker owns that mark, since an interruption only it hears
// drops it too. `silence` cuts whatever is queued to sound and drops the mark.
export type SpeakerShape = {
  readonly placed: () => number;
  readonly play: (notes: ReadonlyArray<Song.Note>, until: number, offset: number) => void;
  readonly silence: () => void;
};

export class Speaker extends Context.Service<Speaker, SpeakerShape>()("@beatdig/client/Speaker") {}

// Eight pings a quarter second apart give an estimate within two seconds of connecting; one every
// two seconds after that follows drift and a changing route.
const BURST = 8;
const BURST_SPACING = "250 millis";
const STEADY_SPACING = "2 seconds";

const cadence = Schedule.concat(
  // upTo counts outputs after the first run, which is the first ping.
  Schedule.spaced(BURST_SPACING).pipe(Schedule.upTo({ times: BURST - 1 })),
  Schedule.spaced(STEADY_SPACING),
);

// Sound is handed over two seconds ahead, a quarter second at a time: a hidden tab's timers fire
// once a second at most, so even a tick that slips leaves a second of sound already placed.
const LOOKAHEAD = 2_000;
const CONDUCT_SPACING = "250 millis";

type State = {
  readonly readings: ReadonlyArray<GameClock.Reading>;
  readonly world: Dig.World;
  readonly you: number;
  readonly draft: string;
};

// What a server message does to what the session knows; `t3` is when the frame arrived.
const apply = (state: State, message: Protocol.ServerMessage, t3: number): State => {
  switch (message.type) {
    case "pong":
      return {
        ...state,
        readings: GameClock.remember(
          state.readings,
          GameClock.read({ t0: message.t0, t1: message.t1, t2: message.t2, t3 }),
        ),
      };
    case "world":
      return {
        ...state,
        world: { started: message.started, players: message.players, ground: message.ground },
        you: message.you,
      };
  }
  return message satisfies never;
};

const digFor = (key: string): Dig.Direction | undefined => {
  switch (key) {
    case "ArrowDown":
      return "down";
    case "ArrowLeft":
      return "left";
    case "ArrowRight":
      return "right";
    default:
      return undefined;
  }
};

type Pressed = {
  readonly state: State;
  readonly message?: Protocol.ClientMessage;
};

// What a key does. Someone in the dig swings with the arrows once it has begun; someone on the
// menu spells a name, and Enter readies them up under it. Until the room has said which player is
// this screen's, a key does nothing: there is no one yet to press it for.
const press = (state: State, key: string): Pressed => {
  const you = state.world.players.find((player) => player.slot === state.you);
  if (you === undefined) {
    return { state };
  }
  if (you.ready) {
    const direction = digFor(key);
    if (!state.world.started || direction === undefined) {
      return { state };
    }
    return { state, message: { type: "dig", direction } };
  }
  switch (key) {
    case "Enter": {
      const name = state.draft.trim();
      return name === "" ? { state } : { state, message: { type: "ready", name } };
    }
    case "Backspace":
      return { state: { ...state, draft: state.draft.slice(0, -1) } };
  }
  // A key that types is one character; the others (Shift, Tab, ArrowUp) are named in words.
  if (key.length !== 1 || state.draft.length >= Protocol.NAME_MAX) {
    return { state };
  }
  return { state: { ...state, draft: state.draft + key } };
};

const view = (state: State): View => {
  const estimate = GameClock.estimate(state.readings);
  return {
    world: state.world,
    you: state.you,
    draft: state.draft,
    offset: estimate.offset,
    rtt: estimate.rtt,
    samples: estimate.samples,
  };
};

// One connection's life: ping on the cadence, fold every server message and every key into the
// state and show the screen the result, send the room what a key asks of it, and keep the speaker
// two seconds ahead in the song once the clock is known and you are in a dig that has begun. Ends
// when the link closes, a write is refused, or the server sends something this client cannot read,
// and silences the speaker as it does.
export const session: Effect.Effect<
  void,
  Errors.Disconnected | Errors.BadMessage | Errors.SendFailed,
  Link | Screen | Speaker | Controls
> = Effect.gen(function* () {
  const link = yield* Link;
  const screen = yield* Screen;
  const speaker = yield* Speaker;
  const controls = yield* Controls;
  let state: State = { readings: [], world: Dig.EMPTY, you: 0, draft: "" };

  const ping = Effect.gen(function* () {
    const t0 = yield* Clock.currentTimeMillis;
    yield* link.send(Protocol.encodeClientMessage({ type: "ping", t0 }));
  });

  const receive = Stream.runForEach(link.messages, (frame) =>
    Effect.gen(function* () {
      const message = yield* Protocol.decodeServerMessage(frame.text);
      state = apply(state, message, frame.at);
      screen.show(view(state));
    }),
  );

  const type = Stream.runForEach(controls.keys, (key) =>
    Effect.gen(function* () {
      const pressed = press(state, key);
      state = pressed.state;
      screen.show(view(state));
      if (pressed.message !== undefined) {
        yield* link.send(Protocol.encodeClientMessage(pressed.message));
      }
    }),
  );

  // The song waits in the lobby: it plays once you are in a dig that has begun. From where the
  // speaker has the song, or from now if the estimate has moved on past that. An estimate that
  // has fallen back reaches less far than the speaker already has, which is an empty stretch:
  // nothing is placed twice.
  const conduct = Effect.gen(function* () {
    const estimate = GameClock.estimate(state.readings);
    const you = state.world.players.find((player) => player.slot === state.you);
    if (estimate.samples === 0 || you === undefined || !you.ready || !state.world.started) {
      return;
    }
    const serverNow = (yield* Clock.currentTimeMillis) + estimate.offset;
    const until = serverNow + LOOKAHEAD;
    const from = Math.max(speaker.placed(), serverNow);
    speaker.play(Song.notesBetween(Song.SONG, from, until), until, estimate.offset);
  });

  // The sound ends with the connection: the next one seats you afresh in the lobby, where the
  // song does not play, so what is still queued would sound over the menu.
  yield* Effect.raceAllFirst([
    Effect.repeat(ping, cadence),
    receive,
    type,
    Effect.repeat(conduct, Schedule.spaced(CONDUCT_SPACING)),
  ]).pipe(Effect.ensuring(Effect.sync(() => speaker.silence())));
});
